import * as React from 'react';

import { I18nextProvider } from 'react-i18next';
import { ExperimentsProvider } from '@wix/wix-experiments-react';
import { WixStyleParamsProvider } from '@wix/editor-settings';
import { TPAComponentsProvider } from 'wix-ui-tpa/TPAComponentsConfig';

import i18n from '../../config/i18n';

import {
  FeedControllerProps,
  GroupControllerProps,
} from '../../controllers/types';
import { Tab } from '../../controllers/group/tab';
import { Group } from './Group';
import {
  AppData,
  AppDataContext,
  GroupActionsProvider,
  GroupContextProvider,
  SiteMembersContext,
  AppSettingsContext,
  WithAppSettingsProps,
} from './Context';
import { MembersControllerProps } from '../../controllers/members';
import { MainControllerProps } from '../../controllers/main';
import {
  ConsoleLogger,
  LogLevel,
} from '@wix/social-groups-common/dist/src/loggers';
import {
  Anonymous,
  canSeeGroup,
  Group as GroupModel,
  isJoined,
} from '@wix/social-groups-api';
import { ApiTypes } from '@wix/social-groups-api/dist/src/types';
import { LoggersContext } from './Context/logger';
import { ActivityControllerProps } from '../../controllers/activity/ActivityController';
import { ActivityContext } from './Context/Activity';
import {
  AppToastsProvider,
  CommonTPAComponentsProvider,
  GroupNotFound,
} from '@wix/social-groups-common';
import { WithAppToastsProps } from '@wix/social-groups-common/dist/src/types';
import {
  MemberInvitesControllerProps,
  TranslationDataProps,
} from '@wix/social-groups-common/dist/src/controllers';
import {
  NotificationSettings,
  NotificationSettingsContext,
} from './Context/NotificationSettings';
import {
  MemberInvitesProvider,
  BiContextProvider,
  WithBiLoggerProps,
  tryToCallBi,
  BIUserEntry,
} from '@wix/social-groups-common/dist/src/context';
import { groupNotFound } from './Context/ErrorHandler/helpers';
import { ErrorHandlerProps } from '../../controllers/errorHandler/ErrorHandlerProps';
import { ErrorHandlerContext } from './Context/ErrorHandler/ErrorHandlerContext';
import { UpdateProgress } from '@wix/social-groups-common/dist/src/components/ContentEditor/UpdateProgress';
import { LeaveGroupDialog } from './Dialogs/LeaveGroupDialog';
import { WithdrawJoinRequestDialog } from './Dialogs/WithdrawJoinRequest';
import { JoinDialog } from './Dialogs/JoinDialog';

const enum OpenedDialog {
  WITHDRAW_JOIN_REQUEST = 'cancel join request',
  LEAVE_GROUP = 'leave group',
  JOIN_DIALOG = 'join dialog',
}

export interface GroupWidgetState {
  error: null | string;
  openDialog: OpenedDialog;
}

export interface GroupWidgetProps
  extends GroupControllerProps,
    FeedControllerProps,
    MembersControllerProps,
    MemberInvitesControllerProps,
    WithAppToastsProps,
    MainControllerProps,
    ActivityControllerProps,
    NotificationSettings,
    AppData,
    WithAppSettingsProps,
    WithBiLoggerProps,
    TranslationDataProps,
    ErrorHandlerProps {}

export class GroupWidget extends React.Component<
  GroupWidgetProps,
  GroupWidgetState
> {
  readonly state: GroupWidgetState = {
    error: null,
    openDialog: null,
  };

  constructor(props: GroupWidgetProps, context: any) {
    super(props, context);
  }

  componentDidMount(): void {
    this.setupLocale(this.props.locale);
  }

  componentWillReceiveProps(
    nextProps: Readonly<GroupWidgetProps>,
    nextContext: any,
  ): void {
    if (nextProps.locale !== this.props.locale) {
      this.setupLocale(nextProps.locale);
    }
    if (nextProps.logLevel !== this.props.logLevel) {
      ConsoleLogger.mode = nextProps.logLevel;
    }
  }

  private setupLocale(lcl) {
    // TODO: make `moment/locale/${lcl}` work
    // + keep in mind that webpack's dynamic `import` does not work for SSR
  }

  componentDidCatch(error, info) {
    console.error(error, info);
  }

  static getDerivedStateFromError(error) {
    return {
      error,
    };
  }

  static defaultProps: Partial<GroupWidgetProps> = {
    isLoggedIn: false,
    promptLogin() {},
    actions: {} as any,
    activeTab: Tab.DISCUSSION,
    currentMember: Anonymous,
    experiments: {},
    group: GroupModel.createEmpty(),
    loading: false,
    locale: '',
    logLevel: LogLevel.NONE,
    memberInvitesLink: '',
    members: [],
    membersActions: {} as any,
    siteMembers: [],
    siteMembersMap: {},
    style: {} as any,
    uploadedRegistry: [],
    externalVideosMetadataRegistry: [],
    changeTab(): void {},
    commentFeedItem(): void {},
    createFeedItem(): void {},
    updateFeedItem(): void {},
    deleteFeedItem(): void {},
    pinFeedItem(): void {},
    unpinFeedItem(): void {},
    followFeedItem(): void {},
    unfollowFeedItem(): void {},
    updateProgress: UpdateProgress.STALE,
  };

  render() {
    const {
      appSettings,
      experiments,
      host,
      language,
      translation,
      biLogger,
    } = this.props;
    const { style, formFactor } = host;
    const isMobile = formFactor === 'Mobile';

    if (this.state.error !== null) {
      return (
        <>
          <h1>Ooops!</h1>
          <div>{this.state.error}</div>
        </>
      );
    }

    return (
      <I18nextProvider i18n={i18n({ language, translation })}>
        <ExperimentsProvider options={{ experiments }}>
          <TPAComponentsProvider value={{ mobile: isMobile }}>
            <CommonTPAComponentsProvider value={{ mobile: isMobile }}>
              <AppSettingsContext.Provider value={appSettings}>
                <WixStyleParamsProvider {...style}>
                  <LoggersContext.Provider value={{ logger: ConsoleLogger }}>
                    <BiContextProvider value={biLogger}>
                      {this.renderGroup()}
                    </BiContextProvider>
                  </LoggersContext.Provider>
                </WixStyleParamsProvider>
              </AppSettingsContext.Provider>
            </CommonTPAComponentsProvider>
          </TPAComponentsProvider>
        </ExperimentsProvider>
      </I18nextProvider>
    );
  }

  private renderGroup() {
    const { groupErrorCode, errorHandlers, host } = this.props;
    if (groupNotFound(groupErrorCode)) {
      const height = host.dimensions.height ? host.dimensions.height : 'auto';
      return (
        <div style={{ height }}>
          <GroupNotFound goToGroupList={errorHandlers.goToGroupList} />
        </div>
      );
    }
    const {
      actions,
      style,
      isRTL,
      cursor,
      contextToken,
      fetchMore,
      activeTab,
      activities,
      activityActions,
      apps,
      changeTab,
      commentFeedItem,
      createFeedItem,
      currentMember,
      pendingMembers,
      currentSiteMember,
      deleteFeedItem,
      feedItemId,
      feedItems,
      followFeedItem,
      group,
      rules,
      isFeedItemCreating,
      isLoggedIn,
      loading,
      feedLoading,
      memberInvitesLink,
      members,
      membersActions,
      membersQueryResponse,
      newMembers,
      notificationActions,
      notificationSettings,
      pinFeedItem,
      promptLogin,
      reactFeedItem,
      siteMembers,
      siteMembersMap,
      toasts,
      unfollowFeedItem,
      unpinFeedItem,
      unreactFeedItem,
      updateFeedItem,
      updateProgress,
      uploadedRegistry,
      externalVideosMetadataRegistry,
      appDefinitionId,
      instance,
      location,
    } = this.props;
    return (
      <ErrorHandlerContext.Provider value={{ groupErrorCode, errorHandlers }}>
        <AppDataContext.Provider
          value={{
            appDefinitionId,
            instance,
            style,
            isRTL,
            host,
            location,
          }}
        >
          <GroupContextProvider
            value={{
              group,
              members,
              rules,
              currentMember,
              uploadedRegistry,
              externalVideosMetadataRegistry,
              feed: {
                feedLoading,
                contextToken,
                cursor,
                fetchMore,
                isFeedItemCreating,
                feedItems,
                deleteFeedItem,
                createFeedItem,
                updateFeedItem,
                commentFeedItem,
                pinFeedItem,
                unpinFeedItem,
                followFeedItem,
                unfollowFeedItem,
                reactFeedItem,
                unreactFeedItem,
              },
              promptLogin,
              isLoggedIn,
              updateProgress,
              apps,
            }}
          >
            <GroupActionsProvider
              value={{
                ...actions,
                changeMembership: this.changeMembership,
                openJoinDialog: this.openJoinDialog,
              }}
            >
              <SiteMembersContext.Provider
                value={{
                  siteMembers,
                  siteMembersMap,
                  membersQueryResponse,
                  membersActions,
                  newMembers,
                  currentSiteMember,
                  pendingMembers,
                }}
              >
                <AppToastsProvider value={toasts}>
                  <ActivityContext.Provider
                    value={{ activities, activityActions }}
                  >
                    <NotificationSettingsContext.Provider
                      value={{
                        notificationActions,
                        notificationSettings,
                      }}
                    >
                      <MemberInvitesProvider value={{ memberInvitesLink }}>
                        <Group
                          feedItemId={feedItemId}
                          activeTab={activeTab}
                          changeTab={changeTab}
                          canSeeGroup={canSeeGroup(group)}
                          ready={!!group.groupId}
                          apps={apps}
                          group={group}
                        />
                        {this.renderWithdrawJoinRequestDialog()}
                        {this.renderLeaveDialog()}
                        {this.renderJoinDialog()}
                      </MemberInvitesProvider>
                    </NotificationSettingsContext.Provider>
                  </ActivityContext.Provider>
                </AppToastsProvider>
              </SiteMembersContext.Provider>
            </GroupActionsProvider>
          </GroupContextProvider>
        </AppDataContext.Provider>
      </ErrorHandlerContext.Provider>
    );
  }

  private renderLeaveDialog() {
    const { group } = this.props;
    const groupWrapper = GroupModel.getGroupResponseWrapper(group);

    return (
      <LeaveGroupDialog
        isOpen={this.isOpenDialog(OpenedDialog.LEAVE_GROUP)}
        onRequestClose={this.closeDialog}
        groupId={group.groupId}
        groupTitle={groupWrapper.getTitle()}
        onLeaveGroup={this.handleLeaveGroupConfirm}
      />
    );
  }

  private renderWithdrawJoinRequestDialog() {
    return (
      <WithdrawJoinRequestDialog
        isOpen={this.isOpenDialog(OpenedDialog.WITHDRAW_JOIN_REQUEST)}
        onRequestClose={this.closeDialog}
        onWithdrawJoinRequest={this.handleWithdrawJoinRequest}
      />
    );
  }

  private renderJoinDialog() {
    return (
      <JoinDialog
        isOpen={this.isOpenDialog(OpenedDialog.JOIN_DIALOG)}
        onRequestClose={this.closeDialog}
      />
    );
  }

  private readonly changeMembership = (biOrigin: string) => {
    const { group } = this.props;

    switch (group.relationship) {
      case ApiTypes.v1.RelationshipWithGroup.REJECTED_MEMBERSHIP:
      case ApiTypes.v1.RelationshipWithGroup.NONE:
        return this.handleJoinGroup(biOrigin);
      case ApiTypes.v1.RelationshipWithGroup.JOINED:
        return this.handleLeaveGroupClick(biOrigin);
      case ApiTypes.v1.RelationshipWithGroup.PENDING_APPROVAL:
        return this.openWithdrawJoinRequestDialog();
      default:
        console.warn('Unknown group relationship');
    }
  };

  private readonly openJoinDialog = () =>
    this.openDialog(OpenedDialog.JOIN_DIALOG);
  private readonly openLeaveDialog = () =>
    this.openDialog(OpenedDialog.LEAVE_GROUP);
  private readonly openWithdrawJoinRequestDialog = () =>
    this.openDialog(OpenedDialog.WITHDRAW_JOIN_REQUEST);
  private readonly closeDialog = () => this.setState({ openDialog: null });
  private readonly openDialog = dialog => this.setState({ openDialog: dialog });
  private isOpenDialog(dialog): boolean {
    return this.state.openDialog === dialog;
  }

  private readonly handleLeaveGroupConfirm = () => {
    this.props.actions.leaveGroup();
    this.closeDialog();
  };

  private readonly handleWithdrawJoinRequest = () => {
    this.props.actions.withdrawJoinRequest();
    this.closeDialog();
  };

  private readonly handleJoinGroup = (biOrigin: string) => {
    const { group, biLogger, actions } = this.props;
    if (!isJoined(group)) {
      tryToCallBi(async () => {
        await biLogger.groupsRequestJoinAGroup({
          groupId: group.approvalStatus, // until dedicated field is added
          group_id: group.groupId,
          origin: biOrigin,
          type: group.settings.privacyLevel,
        });
      });
    }
    actions.joinGroup();
    this.closeDialog();
  };

  private readonly handleLeaveGroupClick = (biOrigin: string) => {
    const { group, biLogger } = this.props;
    tryToCallBi(async () => {
      await biLogger.groupActionClick({
        action: 'leave',
        group_id: group.groupId,
        origin: biOrigin,
        userEntry: BIUserEntry.SITE,
      });
    });
    this.openLeaveDialog();
  };
}
